import { useState, useEffect, useRef } from 'react';
import { FaCode, FaMicrochip, FaGlobe, FaBrain, FaFlask, FaTools, FaExternalLinkAlt, FaFileAlt } from 'react-icons/fa';
import './Projects.css';

import paper3D from '../projects/3D Heat Conduction on Metal Plates.pdf';
import paperAmbient from '../projects/Ambient paper.pdf';
import posterAmbient from '../projects/Ambient poster.pdf';
import paperElevator from '../projects/ELEVATOR FPGA_IEEEpaper-Format.docx (1).pdf';
import paperMicroLab from '../projects/MicroLab Inventory Management System.pdf';
import paperSecureDigit from '../projects/Secure Digital Lock System.pdf';
import paperSignLang from '../projects/sign language paper.pdf';
import paperSmartRoom from '../projects/Smart Room Access.pdf';
import paperSnake from '../projects/Snake & Ladder Game.pdf';
import paperSumo from '../projects/Sumo bot.pdf';
import paperTwoDigit from '../projects/Two-Digit Decimal Counter PCB Design.pdf';
import paperVoice from '../projects/voice controlled.pdf';
import paperWebsite from '../projects/website.pdf';

import img3DHeat from '../projects/3d heat.png';
import imgAmbient from '../projects/ambient pic.jpg';
import imgElevator from '../projects/elevator pic.png';
import imgMicroLab from '../projects/miclab inventory pic.png';
import imgPCB from '../projects/pcb layout picture.png';
import imgSignLang from '../projects/sign language pic.png';
import imgSmartRoom from '../projects/SmartRoom picture.png';
import imgSnake from '../projects/snake and ladder.png';
import imgSumo from '../projects/sumobot pic.png';
import imgVoice from '../projects/Voice-Controlled Lpicture.jpeg';
import imgWebsite from '../projects/website picture.png';
import imgSHSProject from '../projects/shs project.png';
import imgBestPic from '../projects/best pic.jpg';
import imgPacman from '../projects/pacman.jpg';
import imgProjectCover from '../cover/project.JPG';

function Projects() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedMedia, setSelectedMedia] = useState(null); // { type: 'image' | 'pdf', url: string }
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const rawProjects = [
    {
      title: 'Ambient Light-Based Smart Lighting System',
      description: 'An IoT-based smart lighting system that automatically adjusts lighting based on ambient light conditions using sensors and microcontrollers.',
      tech: ['Arduino', 'IoT', 'Sensors', 'C++'],
      category: 'Embedded',
      course: 'CPE-FCS',
      icon: <FaMicrochip />,
      paper: paperAmbient,
      poster: posterAmbient,
      image: imgAmbient
    },
    {
      title: 'Sign Language Recognition System',
      description: 'A Python-based machine learning system that recognizes sign language gestures in real-time using computer vision and trained models.',
      tech: ['Python', 'OpenCV', 'ML', 'MediaPipe'],
      category: 'ML',
      course: 'CPE-CP2',
      icon: <FaBrain />,
      paper: paperSignLang,
      image: imgSignLang
    },
    {
      title: 'Restaurant Web Application',
      description: 'A full-stack restaurant web application with menu browsing, ordering system, and responsive design using modern web technologies.',
      tech: ['HTML/CSS', 'JavaScript', 'React', 'Node.js'],
      category: 'Web',
      course: 'CPE-DS',
      icon: <FaGlobe />,
      paper: paperWebsite,
      image: imgWebsite
    },
    {
      title: 'Voice-Controlled LED System',
      description: 'An embedded system using ESP32 and FFT (Fast Fourier Transform) for voice frequency detection to control LED outputs.',
      tech: ['ESP32', 'FFT', 'C++', 'DSP'],
      category: 'Embedded',
      course: 'ECE-DSP',
      icon: <FaMicrochip />,
      paper: paperVoice,
      image: imgVoice
    },
    {
      title: 'Smart Room Access & Status Indication',
      description: 'An embedded system for automated room access control with real-time status indication using sensors and microcontrollers.',
      tech: ['Arduino', 'Sensors', 'C++', 'RFID'],
      category: 'Embedded',
      course: 'CPE-ES',
      icon: <FaMicrochip />,
      paper: paperSmartRoom,
      image: imgSmartRoom
    },
    {
      title: 'MicroLab Inventory Management System',
      description: 'An object-oriented inventory management system designed for laboratory equipment tracking and management.',
      tech: ['Java', 'OOP', 'GUI', 'Database'],
      category: 'Web',
      course: 'CPE-OOP',
      icon: <FaCode />,
      paper: paperMicroLab,
      image: imgMicroLab
    },
    {
      title: '3D Heat Conduction on Metal Plates',
      description: 'A computational mathematics project simulating 3D heat conduction on metal plates using Partial Differential Equations.',
      tech: ['MATLAB', 'PDE', 'Simulation', '3D Modeling'],
      category: 'Research',
      course: 'CPE-MAT',
      icon: <FaFlask />,
      paper: paper3D,
      image: img3DHeat
    },
    {
      title: 'Two-Digit Decimal Counter PCB Design',
      description: 'A digital electronics project involving PCB design and fabrication of a two-digit decimal counter circuit.',
      tech: ['PCB Design', 'Proteus', 'Digital Logic', 'Soldering'],
      category: 'Hardware',
      course: 'CPE-DES',
      icon: <FaTools />,
      paper: paperTwoDigit,
      image: imgPCB
    },
    {
      title: 'Snake & Ladder Game',
      description: 'A console-based Snake & Ladder game developed as an introductory programming project using fundamental concepts.',
      tech: ['C++', 'Console', 'Game Logic'],
      category: 'Web',
      course: 'CPE-CP1',
      icon: <FaCode />,
      paper: paperSnake,
      image: imgSnake
    },
    {
      title: 'Sumo Bot',
      description: 'An autonomous robot designed for sumo bot competitions, equipped with sensors for opponent detection and arena edge sensing.',
      tech: ['Arduino', 'Sensors', 'Motors', 'C++'],
      category: 'Embedded',
      course: 'CPE-ES',
      icon: <FaMicrochip />,
      paper: paperSumo,
      image: imgSumo
    },
    {
      title: 'Elevator FPGA',
      description: 'An FPGA-based elevator system implementation including control logic and state management.',
      tech: ['Verilog', 'FPGA', 'Digital Design'],
      category: 'Hardware',
      course: 'CPE-HDL',
      icon: <FaTools />,
      paper: paperElevator,
      image: imgElevator
    },
    {
      title: 'Secure Digital Lock System',
      description: 'A hardware description language project implementing a secure digital lock with ABV (Access-Based Verification) on FPGA.',
      tech: ['Verilog', 'FPGA', 'HDL', 'Digital Design'],
      category: 'Hardware',
      course: 'CPE-HDL',
      icon: <FaTools />,
      paper: paperSecureDigit
    },
    {
      title: 'Website Project (HTML)',
      description: 'A comprehensive creative website project developed during Senior High School using fundamental HTML and CSS.',
      tech: ['HTML', 'CSS', 'UI/UX'],
      category: 'Web',
      course: 'SHS-STEM',
      icon: <FaGlobe />,
      image: imgSHSProject,
      noLinks: true
    },
    {
      title: 'Pacman Game on FPGA',
      description: 'A classic Pacman game implementation on an FPGA board, featuring real-time graphics and game logic.',
      tech: ['Verilog', 'FPGA', 'Digital Design', 'VGA'],
      category: 'Hardware',
      course: 'CPE-MIC2',
      icon: <FaMicrochip />,
      image: imgPacman,
      noLinks: true
    },
    {
      title: 'Graduate Tracer Study of CPE Graduates',
      description: 'A comprehensive research study tracking the career paths and outcomes of Computer Engineering graduates.',
      tech: ['Research', 'Data Analysis', 'Survey', 'Documentation'],
      category: 'Research',
      course: 'Research',
      icon: <FaFlask />,
      noLinks: true
    },
  ];

  // Re-arrange projects: those without images go last
  const projects = [
    ...rawProjects.filter(p => p.image),
    ...rawProjects.filter(p => !p.image)
  ];

  const categories = ['All', 'Web', 'Embedded', 'ML', 'Hardware', 'Research'];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  const renderCard = (project, index, isSmall) => (
    <div
      key={project.title}
      className={`project-card ${isSmall ? 'project-card-small' : ''}`}
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      {project.image && (
        <div className="project-card-image-wrapper" onClick={() => setSelectedMedia({ type: 'image', url: project.image, title: project.title })}>
          <img src={project.image} alt={project.title} className="project-card-image" />
          <div className="project-image-overlay">
            <span>Click to view full image</span>
          </div>
        </div>
      )}
      <div className="project-card-content">
        <div className="project-card-header">
          <div className="project-icon">{project.icon}</div>
          <span className="project-course-tag">{project.course}</span>
        </div>
        <h3 className="project-title">{project.title}</h3>
        <p className="project-desc">{project.description}</p>
        <div className="project-tech-tags">
          {project.tech.map((t, i) => (
            <span key={i} className="tech-tag">{t}</span>
          ))}
        </div>
        <div className="project-links">
          {project.paper && (
            <button onClick={() => setSelectedMedia({ type: 'pdf', url: project.paper, title: `${project.title} Paper` })} className="project-link" title="Paper">
              <FaFileAlt /> Paper
            </button>
          )}
          {project.poster && (
            <button onClick={() => setSelectedMedia({ type: 'pdf', url: project.poster, title: `${project.title} Poster` })} className="project-link" title="Poster">
              <FaFileAlt /> Poster
            </button>
          )}
          {(!project.paper && !project.poster && !project.noLinks) && (
            <>
              <a href="#" className="project-link" title="Demo">
                <FaExternalLinkAlt /> Demo
              </a>
            </>
          )}
        </div>
      </div>
      <div className="project-card-glow"></div>
    </div>
  );

  const renderProjectGrid = () => {
    const renderedElements = [];
    let i = 0;
    while (i < filteredProjects.length) {
      const project = filteredProjects[i];
      if (!project.image) {
        let group = [project];
        let j = i + 1;
        while (j < filteredProjects.length && !filteredProjects[j].image && group.length < 2) {
          group.push(filteredProjects[j]);
          j++;
        }
        if (group.length === 2) {
          renderedElements.push(
            <div key={`group-${i}`} className="project-group-column">
              {group.map((gp, idx) => renderCard(gp, i + idx, true))}
            </div>
          );
          i += 2;
        } else {
          renderedElements.push(renderCard(project, i, false));
          i++;
        }
      } else {
        renderedElements.push(renderCard(project, i, false));
        i++;
      }
    }
    return renderedElements;
  };

  return (
    <section className="projects-page">
      {/* Hero Header */}
      <div
        className={`projects-hero ${visibleSections.has('header') ? 'visible' : ''}`}
        data-section="header"
        ref={(el) => (sectionRefs.current[0] = el)}
      >
        <div className="hero-cover-wrapper">
          <img src={imgProjectCover} alt="Projects Cover" className="hero-cover-img" />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content text-right">
          <h1 className="hero-title">Technical Projects</h1>
          <p className="hero-subtitle">Engineering solutions and software applications developed through academic and research initiatives</p>
        </div>
        
        <div className="hero-scroll-indicator">
          <div className="hero-mouse"></div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="projects-body">
        <div className="projects-container">
          <div
            className={`filter-section ${visibleSections.has('filters') ? 'visible' : ''}`}
            data-section="filters"
            ref={(el) => (sectionRefs.current[1] = el)}
          >
            <div className="filter-bar">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
                  onClick={() => setActiveFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Projects Grid */}
          <div
            className={`projects-grid-section ${visibleSections.has('grid') ? 'visible' : ''}`}
            data-section="grid"
            ref={(el) => (sectionRefs.current[2] = el)}
          >
            <div className="projects-grid">
              {renderProjectGrid()}
            </div>
          </div>
        </div>
      </div>

      {/* Global Media Modal */}
      {selectedMedia && (
        <div className="projects-modal-overlay" onClick={() => setSelectedMedia(null)}>
          <div className="projects-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="projects-modal-close" onClick={() => setSelectedMedia(null)}>&times;</button>
            <div className="projects-modal-header">
              <h3>{selectedMedia.title}</h3>
            </div>
            <div className="projects-modal-content">
              {selectedMedia.type === 'image' ? (
                <img src={selectedMedia.url} alt="Full Screen Layout" className="projects-modal-image" />
              ) : (
                <iframe src={selectedMedia.url} title="PDF Viewer" className="projects-modal-pdf" allowFullScreen />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Projects;
